import { CheckboxFormField, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import Typography from 'components/foundation/text/Typography/Typography';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import SectionHeader from 'containers/settings/components/SectionHeader/SectionHeader';
import BudgetFormFields from 'containers/settings/positions/components/BudgetFormFields/BudgetFormFields';
import { compact } from 'lodash';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import { PositionManageBudgetType } from 'utils/positionUtils';
import { ICreatePositionsFormData } from '../CreatePositionForm';

export interface ICreatePositionCompanyManagementSectionProps {
  form: IFormRender<ICreatePositionsFormData>;
}

const CreatePositionCompanyManagementSection = ({ form }: ICreatePositionCompanyManagementSectionProps) => {
  const dailyReportIsEnabled = useFeatureFlags('DAILY_REPORTS');
  const isBulletinEnabled = useFeatureFlags('BULLETIN_FEATURE');
  const isCertificationsEnabled = useFeatureFlags('CERTIFICATIONS');
  const isPro = useFeatureFlags('PRO');
  const hasAccessToBulletin = isBulletinEnabled && isPro;

  const companyManagementDetails = compact([
    {
      name: 'manageCompanySettings',
      title: t('Manage Company Settings'),
      detail: t('View and edit organization-wide features and settings, including pay period and overtime settings.'),
    },
    {
      name: 'manageProjects',
      title: t('Manage Projects'),
      detail: t('View, create, edit, and archive projects and subprojects; create and manage project groups.'),
    },
    {
      name: 'manageCostCodes',
      title: t('Manage Cost Codes'),
      detail: t('View, create, edit, and archive cost codes; create and manage cost code groups.'),
    },
    {
      name: 'manageEquipment',
      title: t('Manage Equipment'),
      detail: t('View, create, edit, and delete equipment; view equipment reports.'),
    },
    isCertificationsEnabled && {
      name: 'manageCertificates',
      title: t('Manage Certifications'),
      detail: t('View, edit, create, and delete employee certifications and certification types.'),
    },
    {
      name: 'viewExpenses',
      title: t('View Expenses'),
      detail: t('View labor costs for employees, projects, cost codes, and equipment; view payroll information.'),
    },
    {
      name: 'manageEmployeeGroups',
      title: t('Manage Employee Groups'),
      detail: t('View, create, and manage employee groups.'),
    },
    dailyReportIsEnabled && {
      name: 'dailyProjectPrompts',
      title: t('Receive Field Report Prompts'),
      detail: t('Receive reminder notification at clock out to create a field report.'),
    },
    hasAccessToBulletin && {
      name: 'manageBulletinPosts',
      title: t('Manage Company Bulletin'),
      detail: t('Add, edit, and delete their own posts from the company bulletin.'),
    },
  ]);

  return (
    <>
      <SectionHeader className="mb-6">{t('Company Management')}</SectionHeader>
      {companyManagementDetails.map((checkPermission) => (
        <div key={checkPermission.name}>
          <CheckboxFormField
            key={checkPermission.name}
            name={checkPermission.name}
            form={form}
            label={
              <HeaderText title={checkPermission.title}>
                <span>{checkPermission.detail}</span>
              </HeaderText>
            }
            disabled={
              (checkPermission.name === 'manageEmployeeGroups' && form.state.data.scopeByGroup) ||
              (checkPermission.name === 'manageIncident' && !form.state.data.createIncident)
            }
          />

          {checkPermission.name === 'dailyProjectPrompts' &&
            dailyReportIsEnabled &&
            form.state.data.dailyProjectPrompts && (
              <DashboardRibbon theme={Theme.LIGHT} className="mb-3">
                <CheckboxFormField
                  name="dailyProjectPromptsUseSubprojects"
                  form={form}
                  label={
                    <HeaderText title={t('Assign Reports to Subprojects')}>
                      <span>
                        {t('Receive reminder notification at clock out to create a field report for each subproject.')}
                      </span>
                    </HeaderText>
                  }
                />
              </DashboardRibbon>
            )}
        </div>
      ))}

      <HeaderText title={t('Budgets')} />
      <Typography tag="span" className="mb-4">
        {form.state.data.manageBudgets === PositionManageBudgetType.VIEW_ONLY
          ? t(
              'The employee will be able to view budget information, but won’t be able to create or edit project budgets.'
            )
          : t(
              'View, create, edit and delete project budgets. Optionally allow the user to view, but not modify, budgets.'
            )}
      </Typography>
      <div className="mt-4">
        <BudgetFormFields form={form} />
      </div>
    </>
  );
};

export default CreatePositionCompanyManagementSection;
